import classNames from 'classnames';
import { useSearchParams } from 'react-router-dom';

import { FilterSelect, Segmented, StateMessage } from '@/components/ui/controls';
import { PageHeader } from '@/components/ui/page';
import type { TelegramChat } from '@/types/api';
import { formatRelative, shownEmail } from '@/utils/format';
import { chatKindLabel } from '@/utils/labels';
import { useApi } from '@/utils/use-api';

import ChannelNav from './channel-nav';
import ChatPanel from './chat-panel';
import { ChatState } from './chat-state';

type Filter = 'unlinked' | 'linked' | 'left' | 'all';

const matches = (c: TelegramChat, f: Filter) => {
  switch (f) {
    case 'unlinked':
      return !c.lead_id && !c.left_at;
    case 'linked':
      return Boolean(c.lead_id);
    case 'left':
      return Boolean(c.left_at);
    default:
      return true;
  }
};

const KindIcon = ({ kind }: { kind: TelegramChat['kind'] }) => (
  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-inset text-fg-muted" title={chatKindLabel(kind)}>
    {kind === 'group' || kind === 'assistant_group' ? (
      <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <circle cx="5.5" cy="6" r="2.2" />
        <circle cx="11" cy="6.5" r="1.8" />
        <path d="M1.5 13.5c.6-2.2 2.2-3.4 4-3.4s3.4 1.2 4 3.4M10 10.2c1.6 0 3 .9 3.6 3.1" strokeLinecap="round" />
      </svg>
    ) : (
      <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <circle cx="8" cy="5.5" r="2.5" />
        <path d="M3 14c.7-2.6 2.6-4 5-4s4.3 1.4 5 4" strokeLinecap="round" />
      </svg>
    )}
  </span>
);

/**
 * Conversations (TG-1…8, LEAD-6): the Telegram groups the bot is in and the
 * people who wrote to it directly. A team member confirms who the client is,
 * which links the chat to a lead (or creates the lead). On a phone the filter is a
 * select, and a chat opens full screen (UI-1, UI-2).
 */
const ConversationsPage = () => {
  const [params, setParams] = useSearchParams();
  const filter = (params.get('status') as Filter) || 'unlinked';
  const selected = params.get('chat');
  const { data, error, loading, reload } = useApi<TelegramChat[]>('/telegram/chats');

  const update = (patch: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );

  const chats = data ?? [];
  const count = (f: Filter) => chats.filter((c) => matches(c, f)).length;
  const shown = chats.filter((c) => matches(c, filter));

  return (
    <div className={classNames('transition-[padding]', selected && 'xl:pr-[452px]')}>
      <PageHeader
        title="Conversations"
        description="Telegram groups with clients and direct messages to the bot. Confirm who each client is to link the chat to a lead."
      >
        <ChannelNav />
        <div className="mb-4 md:hidden">
          <FilterSelect label="Show" value={filter} onChange={(v) => update({ status: v === 'unlinked' ? null : v })}>
            <option value="unlinked">Needs confirmation ({count('unlinked')})</option>
            <option value="linked">Linked ({count('linked')})</option>
            <option value="left">Bot left ({count('left')})</option>
            <option value="all">All ({chats.length})</option>
          </FilterSelect>
        </div>
        <div className="mb-4 hidden md:block">
          <Segmented<Filter>
            label="Show"
            value={filter}
            onChange={(v) => update({ status: v === 'unlinked' ? null : v })}
            options={[
              { value: 'unlinked', label: `Needs confirmation (${count('unlinked')})` },
              { value: 'linked', label: `Linked (${count('linked')})` },
              { value: 'left', label: `Bot left (${count('left')})` },
              { value: 'all', label: `All (${chats.length})` },
            ]}
          />
        </div>
      </PageHeader>

      {error && !data ? (
        <StateMessage title="Conversations could not be loaded" tone="error">
          {error}
        </StateMessage>
      ) : data && shown.length === 0 ? (
        <StateMessage title={filter === 'unlinked' ? 'Nothing to confirm' : 'No conversations here'}>
          {filter === 'unlinked'
            ? 'Every chat is linked to a lead. New groups appear here as soon as the bot is added.'
            : 'Add the bot to a Telegram group with a client to start.'}
        </StateMessage>
      ) : (
        <ul className={classNames('overflow-hidden rounded-card bg-surface shadow-e1 transition-opacity', loading && data && 'opacity-60')}>
          {!data &&
            Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className="border-b border-line px-4 py-4 last:border-0">
                <div className="h-4 w-2/3 animate-pulse rounded bg-layer" />
              </li>
            ))}
          {shown.map((c) => (
            <li key={c.chat_id} className="border-b border-line last:border-0">
              <button
                type="button"
                onClick={() => update({ chat: String(c.chat_id) })}
                aria-current={selected === String(c.chat_id)}
                className={classNames(
                  'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                  selected === String(c.chat_id) ? 'bg-brand/10' : 'hover:bg-surface-hover',
                )}
              >
                <KindIcon kind={c.kind} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="truncate text-fg-strong">{c.title || 'Untitled chat'}</p>
                    <span className="shrink-0 text-xs text-fg-faint">{c.last_message_at ? formatRelative(c.last_message_at) : ''}</span>
                  </div>
                  <ChatState chat={c} />
                  {c.member_count !== null && <p className="text-xs text-fg-faint">{c.member_count === 1 ? '1 member' : `${c.member_count} members`}</p>}
                  {c.claimed_email && !c.lead_id && (
                    <p className="text-xs text-fg-muted [overflow-wrap:anywhere]">
                      {c.claimed_via === 'handle' ? (
                        <>@{c.claimed_by_username} has the Telegram handle of {shownEmail(c.claimed_email) || 'a lead without email'}</>
                      ) : (
                        <>
                          Says {c.claimed_email}
                          {c.matching_lead_id ? ' · matches an existing lead' : ' · no lead with this email yet'}
                        </>
                      )}
                    </p>
                  )}
                  {c.last_message_text && (
                    <p className="mt-1 truncate text-sm text-fg-muted">
                      <span className="text-fg">{c.last_message_from ? `${c.last_message_from}: ` : ''}</span>
                      {c.last_message_text}
                    </p>
                  )}
                </div>
                <span className="shrink-0 self-center text-xs tabular-nums text-fg-faint">{c.messages} msgs</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <ChatPanel
          id={selected}
          onClose={() => update({ chat: null })}
          onChanged={reload}
        />
      )}
    </div>
  );
};

export default ConversationsPage;
